import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import Cropper from "react-easy-crop";
import {
  Edit,
  X,
  Save,
  AlertCircle,
  Building,
  Image,
  MapPin,
  Globe,
  Shield,
  School,
  Crop,
  Check,
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import { LMSBASEURL } from "../../config/apiUrls";
import { isMobile } from "react-device-detect";

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    border: "1px solid #ced4da",
    borderRadius: "0.5rem",
    boxShadow: "none",
    minHeight: "42px",
    fontSize: "0.9rem",
    backgroundColor: "#fff",
    "&:hover": { border: "1px solid #6c757d" },
  }),
  placeholder: (provided) => ({ ...provided, color: "#6c757d" }),
  singleValue: (provided) => ({ ...provided, color: "#212529" }),
};

// Helper function to create image from croppedAreaPixels
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new window.Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    }, "image/jpeg");
  });
};

export default function InstituteProfilePage() {
  const APItoken = process.env.REACT_APP_API_TOKEN;
  const instituteId = sessionStorage.getItem("instituteId");
  const AWS = process.env.REACT_APP_AWS_HOST;

  const [isEditing, setIsEditing] = useState(false);
  const [statesOptions, setStatesOptions] = useState([]);
  const [citiesOptions, setCitiesOptions] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Crop modal states
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // New: Track if user chose crop or direct
  const [uploadChoice, setUploadChoice] = useState(null); // 'crop' or 'direct'

  // Broadcast Channel for cross-tab communication
  const [broadcastChannel] = useState(() => 
    typeof BroadcastChannel !== 'undefined' 
      ? new BroadcastChannel('institute_logo_channel') 
      : null
  );

  const [basic, setBasic] = useState({
    instituteName: "",
    status: 0,
    type: null,
    address1: "",
    address2: "",
    stateId: null,
    stateName: "",
    cityId: null,
    cityName: "",
    institute_logo: null,
  });

  // Listen for logo updates from other tabs
  useEffect(() => {
    if (!broadcastChannel) return;

    const handleMessage = (event) => {
      if (event.data.type === 'LOGO_UPDATE') {
        if (event.data.logo) {
          sessionStorage.setItem("instituteLogo", event.data.logo);
        }
        setBasic((prev) => ({ ...prev, institute_logo: event.data.logo }));
      }
    };

    broadcastChannel.addEventListener('message', handleMessage);

    return () => {
      broadcastChannel.removeEventListener('message', handleMessage);
    };
  }, [broadcastChannel]);

  // Cleanup broadcast channel on unmount
  useEffect(() => {
    return () => {
      if (broadcastChannel) {
        broadcastChannel.close();
      }
    };
  }, [broadcastChannel]);

  // Broadcast logo update to other tabs
  const broadcastLogoUpdate = useCallback((logoUrl) => {
    if (broadcastChannel) {
      broadcastChannel.postMessage({
        type: 'LOGO_UPDATE',
        logo: logoUrl,
        timestamp: Date.now()
      });
    }
  }, [broadcastChannel]);

  const fetchState = useCallback(async () => {
    try {
      const response = await fetch(`${LMSBASEURL}/api/get-state`, {
        headers: {
          Authorization: APItoken,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to load states");
      setStatesOptions(
        result?.data?.map(({ id, name }) => ({ label: name, value: id })) || []
      );
    } catch {
      setStatesOptions([]);
    }
  }, [APItoken]);

  const fetchCity = useCallback(
    async (id) => {
      try {
        const response = await fetch(`${LMSBASEURL}/api/get-city?state_id=${id}`, {
          headers: {
            Authorization: APItoken,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || "Failed to load cities");
        setCitiesOptions(
          result?.data?.map(({ id, name }) => ({ label: name, value: id })) || []
        );
      } catch {
        setCitiesOptions([]);
      }
    },
    [APItoken]
  );

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch(
        `${LMSBASEURL}/api/institute/get-institute?instituteId=${instituteId}`,
        {
          headers: {
            Authorization: APItoken,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to load profile");
      const data = result?.data;
      setBasic({
        instituteName: data?.institute_name || "",
        status: data?.status,
        type: data?.type || null,
        address1: data?.address1 || "",
        address2: data?.address2 || "",
        stateId: data?.state?.id || null,
        stateName: data?.state?.name || "",
        cityId: data?.city?.id || null,
        cityName: data?.city?.name || "",
        institute_logo: data?.institute_logo || null,
      });
      
      if (data?.institute_logo) {
        sessionStorage.setItem("instituteLogo", data.institute_logo);
        broadcastLogoUpdate(data.institute_logo);
      }
    } catch {
      setBasic({
        instituteName: "",
        status: "active",
        type: null,
        address1: "",
        address2: "",
        stateId: null,
        stateName: "",
        cityId: null,
        cityName: "",
        institute_logo: null,
      });
    }
  }, [APItoken, instituteId, broadcastLogoUpdate]);

  useEffect(() => {
    if (isEditing) {
      fetchState();
      if (basic.stateId) {
        fetchCity(basic.stateId);
      }
    }
  }, [isEditing, fetchState, fetchCity, basic.stateId]);

  // Initialize logo from session storage on mount
  useEffect(() => {
    const storedLogo = sessionStorage.getItem("instituteLogo");
    if (storedLogo) {
      setBasic((prev) => ({ ...prev, institute_logo: storedLogo }));
    }
    fetchProfile();
  }, [fetchProfile]);

  // Handle file selection
  const handleImageSelect = (e) => {
    if (!isEditing) return;
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageSrc(reader.result);
      setUploadChoice(null); // Reset choice
      setShowCropModal(false);
    };
    reader.readAsDataURL(file);
  };

  // User chooses: Crop or Direct
  const handleUploadChoice = (choice) => {
    setUploadChoice(choice);
    if (choice === 'crop') {
      setShowCropModal(true);
    } else {
      // Direct upload: use original image
      setBasic((prev) => ({ ...prev, institute_logo: imageSrc }));
      setImageSrc(null);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      setBasic((prev) => ({ ...prev, institute_logo: croppedImage }));
      setShowCropModal(false);
      setImageSrc(null);
      setUploadChoice(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (e) {
      console.error("Error cropping image:", e);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageSrc(null);
    setUploadChoice(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onStateSelect = (option) => {
    if (option?.value) {
      fetchCity(option.value);
    } else {
      setCitiesOptions([]);
    }
    setBasic((prev) => ({
      ...prev,
      stateId: option?.value || null,
      cityId: null,
    }));
  };

  const onCitySelect = (option) => {
    setBasic((prev) => ({ ...prev, cityId: option?.value || null }));
  };

  const toggleEditMode = () => {
    setIsEditing((prev) => !prev);
    setError("");
    setImageSrc(null);
    setUploadChoice(null);
  };

  const handleInputChange = (e) => {
    if (!isEditing) return;
    const { name, value } = e.target;
    setBasic((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing) return;
    if (!basic.instituteName || !basic.stateId || !basic.cityId) {
      setError("Please fill all mandatory fields.");
      return;
    }
    try {
      setLoading(true);
      const myHeaders = new Headers();
      myHeaders.append("authorization", APItoken);
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        institute_name: basic?.instituteName,
        country: "India",
        state: basic?.stateId,
        city: basic?.cityId,
        address1: basic?.address1,
        address2: basic?.address2,
        status: basic?.status,
        user_id: sessionStorage?.getItem("id"),
        type: basic?.type,
        institute_logo: basic?.institute_logo,
      });

      const response = await fetch(`${LMSBASEURL}/api/institute/${instituteId}`, {
        method: "PUT",
        headers: myHeaders,
        body: raw,
      });

      if (response.ok) {
        if (basic?.institute_logo) {
          sessionStorage.setItem("instituteLogo", basic.institute_logo);
          broadcastLogoUpdate(basic.institute_logo);
        }
      }

      setIsEditing(false);
      fetchProfile();
    } catch {
      setError("Failed to update institute profile.");
    } finally {
      setLoading(false);
    }
  };

  const renderViewValue = (value, placeholder = "Not provided") => (
    <p className="mb-0 text-dark fw-semibold" style={{ fontSize: "0.95rem" }}>
      {value || placeholder}
    </p>
  );

  return (
    <>
      <PageHeader
        title="Institute Profile"
        breadcrumbs={[
          { label: "Dashboard", path: "/institute-dashboard" },
          { label: "Institute Profile", active: true },
        ]}
      />

      <div className="py-4">
        {error && (
          <div
            className="alert alert-danger d-flex align-items-center mb-4 shadow rounded-3"
            role="alert"
          >
            <AlertCircle className="me-2" size={20} />
            <div>{error}</div>
          </div>
        )}

        {/* Crop Modal */}
        {showCropModal && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <Crop className="me-2" size={20} />
                    Crop Image
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCropCancel}
                  ></button>
                </div>
                <div className="modal-body" style={{ height: "400px", position: "relative" }}>
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={170 / 60}
                    cropShape="rect"
                    showGrid={false}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
                <div className="modal-footer d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2 flex-grow-1">
                    <label className="small text-muted">Zoom:</label>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="form-range flex-grow-1"
                      style={{ maxWidth: "200px" }}
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCropCancel}
                    >
                      <X className="me-1" size={16} />
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleCropSave}
                    >
                      <Check className="me-1" size={16} />
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {/* Card 1: Summary */}
          <div className="card border-1 rounded-4 mb-4">
            <div className="card-body gap-3 p-3">
              <div className="row p-2">
                <div className="col-12 d-md-none mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm rounded-pill float-end"
                    onClick={toggleEditMode}
                    disabled={loading}
                  >
                    {isEditing ? (
                      <>
                        <X className="me-1" size={16} /> Cancel
                      </>
                    ) : (
                      <>
                        <Edit className="me-1" size={16} /> Edit Profile
                      </>
                    )}
                  </button>
                </div>

                <div className="col-md-10">
                  <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 text-center text-md-start">
                      {basic.institute_logo ? (
                          <img
                            src={
                              basic.institute_logo.startsWith("data:")
                                ? basic.institute_logo
                                : `${AWS}${basic.institute_logo}`
                            }
                            alt="Logo"
                            className={`flex-shrink-0 img-fluid ${isMobile ? "w-50" : "col-md-3 col-lg-1"}`}
                          />
                      ) : (
                      <div
                        className="img-fluid bg-primary text-white d-flex align-items-center justify-content-center shadow flex-shrink-0 rounded"
                        style={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                        }}
                      >
                          {basic.instituteName?.charAt(0) || "I"}
                        </div>
                      )}
                    <div className="align-self-center">
                      <h4 className="mb-1 fw-bold">
                        {basic.instituteName || "Institute Profile"}
                      </h4>
                      <p className="mb-0 text-muted small">
                        {basic.address1} {basic.address2 && `, ${basic.address2}`}{" "}
                        {basic.cityName && `${basic.cityName}`}
                        {basic.stateName && `, ${basic.stateName}`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-2 d-none d-md-block">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm rounded-pill float-end"
                    onClick={toggleEditMode}
                    disabled={loading}
                  >
                    {isEditing ? (
                      <>
                        <X className="me-1" size={16} /> Cancel
                      </>
                    ) : (
                      <>
                        <Edit className="me-1" size={16} /> Edit Profile
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Institute Info */}
          <div className="card border-1 rounded-4 mb-4">
            <div className="card-body p-4">
              <h5 className="fw-bold mb-4">Institute Info</h5>

              <div className="row g-4 align-items-center">
                <div className="col-md-4 mb-2 mt-4">
                  <label className="form-label small text-muted fw-medium">
                    <Building className="me-1" size={15} /> Name
                  </label>
                  {isEditing ? (
                    <input
                      name="instituteName"
                      value={basic.instituteName}
                      onChange={handleInputChange}
                      className="form-control rounded-3 shadow-sm"
                      required
                    />
                  ) : (
                    renderViewValue(basic.instituteName)
                  )}
                </div>

                <div className="col-md-4 mb-2 mt-4">
                  <label className="form-label small text-muted fw-medium">
                    <Shield className="me-1" size={15} /> Status
                  </label>
                  {isEditing ? (
                    <div className="d-flex gap-3 mt-1">
                      {[
                        { value: "1", label: "Active" },
                        { value: "0", label: "Inactive" },
                      ].map((opt) => (
                        <div className="form-check" key={opt.value}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="status"
                            value={opt.value}
                            checked={basic.status === opt.value}
                            onChange={handleInputChange}
                          />
                          <label className="form-check-label">{opt.label}</label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    renderViewValue(basic?.status == 1 ? "Active" : "Inactive")
                  )}
                </div>

                <div className="col-md-4 mb-2 mt-4">
                  <label className="form-label small text-muted fw-medium">
                    <School className="me-1" size={15} /> Type
                  </label>
                  {isEditing ? (
                    <div className="d-flex gap-3 mt-1">
                      {[
                        { value: "school", label: "School" },
                        { value: "college", label: "College" },
                      ].map((opt) => (
                        <div className="form-check" key={opt.value}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="type"
                            value={opt.value}
                            checked={basic.type === opt.value}
                            onChange={handleInputChange}
                            disabled
                          />
                          <label className="form-check-label">{opt.label}</label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    renderViewValue(
                      basic.type == null
                        ? "Not Specified"
                        : basic.type.charAt(0).toUpperCase() + basic.type.slice(1)
                    )
                  )}
                </div>
              </div>

              {/* Profile Image Upload with Choice */}
              {isEditing && (
                <div className="col-md-6 mb-2 mt-3">
                  <label className="form-label small text-muted fw-medium">
                    <Image className="me-1" size={15} /> Profile Image
                  </label>

                  {/* Step 1: File Input */}
                  {!imageSrc && !basic.institute_logo?.startsWith("data:") && (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control rounded-3 shadow-sm"
                        onChange={handleImageSelect}
                      />
                      <small className="text-muted">Select an image to upload</small>
                    </>
                  )}

                  {/* Step 2: Preview + Choice */}
                  {imageSrc && !showCropModal && (
                    <div className="mt-3 p-3 border rounded-3 bg-light">
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <img
                          src={imageSrc}
                          alt="Preview"
                          className="rounded"
                          style={{ width: "120px", height: "120px", objectFit: "cover" }}
                        />
                        <div>
                          <p className="mb-1 small fw-medium">Selected Image</p>
                          <p className="mb-0 text-muted" style={{ fontSize: "0.8rem" }}>
                            Choose how to upload:
                          </p>
                        </div>
                      </div>

                      <div className="d-flex gap-3">
                        <button
                          type="button"
                          className={`btn btn-outline-primary btn-sm flex-fill ${uploadChoice === 'crop' ? 'active' : ''}`}
                          onClick={() => handleUploadChoice('crop')}
                        >
                          <Crop size={14} className="me-1" />
                          Crop & Upload
                        </button>
                        <button
                          type="button"
                          className={`btn btn-outline-success btn-sm flex-fill ${uploadChoice === 'direct' ? 'active' : ''}`}
                          onClick={() => handleUploadChoice('direct')}
                        >
                          <Check size={14} className="me-1" />
                          Upload Directly
                        </button>
                      </div>

                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0"
                          onClick={() => {
                            setImageSrc(null);
                            setUploadChoice(null);
                          }}
                        >
                          Change Image
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Show current logo preview if already set */}
                  {basic.institute_logo?.startsWith("data:") && (
                    <div className="mt-3 p-3 border rounded-3 bg-success-subtle">
                      <div className="d-flex align-items-center gap-2">
                        <Check size={16} className="text-success" />
                        <span className="small fw-medium text-white">
                          Image ready to save
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Card 3: Address Info */}
          <div className="card border-1 rounded-4">
            <div className="card-body p-4">
              <h5 className="fw-bold mb-4">Address Info</h5>

              <div className="row g-4">
                <div className="col-md-6">
                  <label className="form-label small text-muted fw-medium">
                    <MapPin className="me-1" size={15} /> Address Line 1
                  </label>
                  {isEditing ? (
                    <input
                      name="address1"
                      value={basic.address1}
                      onChange={handleInputChange}
                      className="form-control rounded-3 shadow-sm"
                      required
                    />
                  ) : (
                    renderViewValue(basic.address1)
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label small text-muted fw-medium">
                    <MapPin className="me-1" size={15} /> Address Line 2
                  </label>
                  {isEditing ? (
                    <input
                      name="address2"
                      value={basic.address2}
                      onChange={handleInputChange}
                      className="form-control rounded-3 shadow-sm"
                    />
                  ) : (
                    renderViewValue(basic.address2)
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label small text-muted fw-medium">
                    <Globe className="me-1" size={15} /> State
                  </label>
                  {isEditing ? (
                    <Select
                      options={statesOptions}
                      value={
                        statesOptions.find((o) => o.value === basic.stateId) || null
                      }
                      onChange={onStateSelect}
                      placeholder="Select a state..."
                      isClearable
                      styles={customSelectStyles}
                    />
                  ) : (
                    renderViewValue(basic.stateName)
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label small text-muted fw-medium">
                    <Globe className="me-1" size={15} /> City
                  </label>
                  {isEditing ? (
                    <Select
                      options={citiesOptions}
                      value={
                        citiesOptions.find((o) => o.value === basic.cityId) || null
                      }
                      onChange={onCitySelect}
                      placeholder={
                        basic.stateId ? "Select a city..." : "Select state first"
                      }
                      isDisabled={!basic.stateId}
                      isClearable
                      styles={customSelectStyles}
                    />
                  ) : (
                    renderViewValue(basic.cityName)
                  )}
                </div>
              </div>
            </div>
          </div>
          <div>
              {isEditing && (
                  <div className="col-12 d-flex justify-content-end gap-3 mt-4">
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-pill px-4"
                      onClick={toggleEditMode}
                      disabled={loading}
                    >
                      <X className="me-1" size={16} /> Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary rounded-pill px-4 shadow-sm"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="me-1" size={16} /> Save Changes
                        </>
                      )}
                    </button>
                  </div>
                )}
          </div>
        </form>

      </div>
    </>
  );
}